import { useState, useEffect, useMemo, useRef } from 'react';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  cohortsApi,
  type DateConstraintUpdate,
  type ModalityUpdate,
} from '../../../../services/api/cohorts';
import { partnersApi } from '../../../../services/api/partners';
import { usersApi, type InternalUser } from '../../../../services/api/users';
import { jobTitlesApi } from '../../../../services/api/jobTitles';
import type {
  CohortWithDetails,
  PartnerContactWithCohortCount,
  CohortStatus,
  ConfigurableOneOnOneType,
  CoachingSubType,
} from '../../../../types';
import { DEFAULT_ONE_ON_ONE_TYPE } from '../../../../types';
import { useJobTitles } from '../../../../hooks/useJobTitles';
import { parseDateOnly, toISODateString } from '../../../../utils/dateHelpers';
import {
  DEFAULT_CHECKIN_SETTINGS,
  checkinsError,
  programWeeks,
} from '../../../../utils/checkinWeeks';
import type {
  ConstraintRow,
  CoachHuddleRow,
  ModalityConfigRow,
  ModalitySessionConfig,
  SessionDateRow,
  EditCohortFormValues,
  WrapDateConflict,
  UseEditCohortFormReturn,
} from './editCohortTypes';
import {
  changedSessionUpdates,
  generateId,
  parseDateTime,
  toNormalizedDateString,
  toISODateTimeString,
} from './editCohortHelpers';
import { clearableCohortFields } from './clearableCohortFields';

/** MED-1269: stable string key for a constraint list, for value-based dirty
 * comparison (order-independent, only the meaningful fields). */
function serializeConstraints(rows: ConstraintRow[]): string {
  return JSON.stringify(
    rows
      .map(c => ({
        s: toNormalizedDateString(c.startDate) ?? '',
        e: toNormalizedDateString(c.endDate) ?? '',
        l: c.label || '',
      }))
      .sort((a, b) => (a.s + a.e + a.l).localeCompare(b.s + b.e + b.l))
  );
}

interface UseEditCohortFormArgs {
  opened: boolean;
  cohort: CohortWithDetails;
  onUpdate: (cohort: CohortWithDetails) => void;
  onClose: () => void;
}

export function useEditCohortForm({
  opened,
  cohort,
  onUpdate,
}: UseEditCohortFormArgs): UseEditCohortFormReturn {
  const { jobTitleGroups } = useJobTitles();
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Data
  const [contacts, setContacts] = useState<PartnerContactWithCohortCount[]>([]);
  const [users, setUsers] = useState<InternalUser[]>([]);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [teamAssignments, setTeamAssignments] = useState<
    Record<string, string | null>
  >({});
  const [dateConstraints, setDateConstraints] = useState<ConstraintRow[]>([]);
  const [coachHuddles, setCoachHuddles] = useState<CoachHuddleRow[]>([]);
  const [sessionDates, setSessionDates] = useState<SessionDateRow[]>([]);
  const [modalityConfigs, setModalityConfigs] = useState<ModalityConfigRow[]>(
    []
  );
  const [originalModalityConfigs, setOriginalModalityConfigs] = useState<
    ModalityConfigRow[]
  >([]);
  // MED-1269: as-loaded baselines for the value-based dirty check (AC8/AC9).
  const [originalContactIds, setOriginalContactIds] = useState<string[]>([]);
  const [originalConstraintsKey, setOriginalConstraintsKey] =
    useState<string>('[]');

  // Form for basic info and cohort dates
  // MED-1360: the values the form loaded. A save sends an optional field only
  // when it changed from these, so it never touches a field the user left.
  const loadedValuesRef = useRef<EditCohortFormValues | null>(null);
  const form = useForm<EditCohortFormValues>({
    initialValues: {
      name: '',
      short_name: '',
      code: '',
      default_meeting_link: '',
      default_meeting_platform: '' as string,
      show_zoom_dial_in: false,
      coach_immersion_date: null as Date | null,
      participant_orientation_date: null as Date | null,
      cohort_wrap_up_date: null as Date | null,
      start_date: null as Date | null,
      end_date: null as Date | null,
      participant_description: '',
      matching_form_link: '',
      matching_form_deadline: null as Date | null,
      info_session_date: null as Date | null,
      checkins: DEFAULT_CHECKIN_SETTINGS,
    },
    validate: {
      name: value => (value.trim() ? null : 'Cohort name is required'),
      short_name: value => (value.trim() ? null : 'Short name is required'),
      code: value => (value.trim() ? null : 'Cohort code is required'),
      default_meeting_link: value => {
        if (!value.trim()) return null;
        try {
          new URL(value.trim());
          return null;
        } catch {
          return 'Please enter a valid URL';
        }
      },
      matching_form_link: value => {
        if (!value.trim()) return null;
        try {
          new URL(value.trim());
          return null;
        } catch {
          return 'Please enter a valid URL';
        }
      },
      cohort_wrap_up_date: () => null, // MED-372: wrap date is optional
    },
  });

  // Track original team assignments for change detection
  const [originalTeamAssignments, setOriginalTeamAssignments] = useState<
    Record<string, string | null>
  >({});

  // Track original values for date change detection
  const [originalDates, setOriginalDates] = useState({
    coach_immersion_date: null as Date | null,
    participant_orientation_date: null as Date | null,
    cohort_wrap_up_date: null as Date | null,
    coach_huddle_dates: [] as Date[],
    session_dates: [] as SessionDateRow[],
  });

  // Detect if any key dates have changed
  const hasDateChanges = useMemo(() => {
    const coachImmersionChanged =
      toNormalizedDateString(form.values.coach_immersion_date) !==
      toNormalizedDateString(originalDates.coach_immersion_date);
    const participantOrientationChanged =
      toNormalizedDateString(form.values.participant_orientation_date) !==
      toNormalizedDateString(originalDates.participant_orientation_date);
    const wrapUpChanged =
      toNormalizedDateString(form.values.cohort_wrap_up_date) !==
      toNormalizedDateString(originalDates.cohort_wrap_up_date);

    const currentHuddleDates = coachHuddles
      .filter(h => h.dateTime)
      .map(h => toNormalizedDateString(h.dateTime))
      .sort();
    const originalHuddleDates = originalDates.coach_huddle_dates
      .map(d => toNormalizedDateString(d))
      .sort();
    const huddlesChanged =
      JSON.stringify(currentHuddleDates) !==
      JSON.stringify(originalHuddleDates);

    const currentSessionDates = sessionDates.map(s => ({
      id: s.id,
      d: toNormalizedDateString(s.date),
    }));
    const originalSessionDates = originalDates.session_dates.map(s => ({
      id: s.id,
      d: toNormalizedDateString(s.date),
    }));
    const sessionsChanged =
      JSON.stringify(currentSessionDates) !==
      JSON.stringify(originalSessionDates);

    return (
      coachImmersionChanged ||
      participantOrientationChanged ||
      wrapUpChanged ||
      huddlesChanged ||
      sessionsChanged
    );
  }, [form.values, coachHuddles, sessionDates, originalDates]);

  // Detect if any team assignments have changed from original values
  const hasTeamAssignmentChanges = useMemo(() => {
    const allKeys = new Set([
      ...Object.keys(teamAssignments),
      ...Object.keys(originalTeamAssignments),
    ]);
    for (const key of allKeys) {
      const current = teamAssignments[key] ?? null;
      const original = originalTeamAssignments[key] ?? null;
      if (current !== original) return true;
    }
    return false;
  }, [teamAssignments, originalTeamAssignments]);

  // MED-1269: modality settings/duration changes, for the dirty check (AC9).
  const hasModalitySettingsChanges = useMemo(() => {
    return modalityConfigs.some(config => {
      const original = originalModalityConfigs.find(o => o.id === config.id);
      if (!original) return false;
      return (
        original.ai_features_enabled !== config.ai_features_enabled ||
        original.live_session_delivery_enabled !==
          config.live_session_delivery_enabled ||
        original.commitment_tracking_enabled !==
          config.commitment_tracking_enabled ||
        original.coaching_sub_type !== config.coaching_sub_type ||
        original.session_duration_minutes !== config.session_duration_minutes ||
        JSON.stringify(config.sessionConfigs) !==
          JSON.stringify(original.sessionConfigs)
      );
    });
  }, [modalityConfigs, originalModalityConfigs]);

  // Get the job title IDs that actually changed (for regeneration scope)
  const changedJobTitleIds = useMemo(() => {
    const changed: string[] = [];
    const allKeys = new Set([
      ...Object.keys(teamAssignments),
      ...Object.keys(originalTeamAssignments),
    ]);
    for (const key of allKeys) {
      const current = teamAssignments[key] ?? null;
      const original = originalTeamAssignments[key] ?? null;
      if (current !== original) changed.push(key);
    }
    return changed;
  }, [teamAssignments, originalTeamAssignments]);

  // Determine if session dates should be locked (lock when group sessions exist)
  const sessionDatesLocked = useMemo(() => {
    if (!cohort.sessions || cohort.sessions.length === 0) return false;
    return cohort.sessions.some(session => session.group_id !== null);
  }, [cohort.sessions]);

  const totalSessionCount = cohort.sessions?.length || 0;

  // Check if session configuration is editable (only Draft or In Prep)
  const editableStatuses: CohortStatus[] = ['draft', 'in_prep'];
  const canEditSessionConfig = editableStatuses.includes(cohort.status);

  // MED-1350: the check-in toggles show only with group or 1:1 coaching.
  const hasCheckinHost = modalityConfigs.some(
    m =>
      m.modality_type === 'group_coaching' || m.modality_type === 'one_on_one'
  );

  // Detect if duration or session configs have changed (for warning)
  const hasDurationChanges = useMemo(() => {
    return modalityConfigs.some((config, index) => {
      const original = originalModalityConfigs[index];
      if (!original) return false;
      if (config.session_duration_minutes !== original.session_duration_minutes)
        return true;
      // Check per-session config changes
      if (config.sessionConfigs.length !== original.sessionConfigs.length)
        return true;
      return config.sessionConfigs.some((sc, i) => {
        const orig = original.sessionConfigs[i];
        return (
          !orig ||
          sc.durationMinutes !== orig.durationMinutes ||
          sc.oneOnOneType !== orig.oneOnOneType
        );
      });
    });
  }, [modalityConfigs, originalModalityConfigs]);

  const showDurationWarning = hasDurationChanges && sessionDatesLocked;

  // MED-234: Detect if session dates exceed the wrap date
  const wrapDateConflict = useMemo<WrapDateConflict | null>(() => {
    const wrapDate = form.values.cohort_wrap_up_date;
    if (!wrapDate) return null;

    let latestSessionDate: Date | null = null;
    for (const session of sessionDates) {
      if (
        session.date &&
        (!latestSessionDate || session.date > latestSessionDate)
      ) {
        latestSessionDate = session.date;
      }
    }

    if (!latestSessionDate || latestSessionDate <= wrapDate) return null;

    const suggestedDate = new Date(latestSessionDate);
    suggestedDate.setDate(suggestedDate.getDate() + 7);

    const formatDate = (d: Date) =>
      d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

    return {
      wrapDateFormatted: formatDate(wrapDate),
      suggestedDateFormatted: formatDate(suggestedDate),
    };
  }, [form.values.cohort_wrap_up_date, sessionDates]);

  // Get unique session numbers for deduplicated display when locked
  const uniqueSessionNumbers = useMemo(() => {
    if (!cohort.sessions || cohort.sessions.length === 0) return [];
    const sessionNumbers = [
      ...new Set(cohort.sessions.map(s => s.session_number)),
    ];
    return sessionNumbers.sort((a, b) => a - b);
  }, [cohort.sessions]);

  // ========================
  // Data Fetching
  // ========================

  const fetchContacts = async () => {
    setLoadingContacts(true);
    try {
      const data = await partnersApi.listContacts(cohort.partner_id);
      setContacts(data);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load partner contacts',
        color: 'red',
      });
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const data = await usersApi.listInternal();
      setUsers(data);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load users',
        color: 'red',
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  // Initialize form and state when modal opens
  useEffect(() => {
    if (opened && cohort) {
      form.clearErrors();
      const loadedValues: EditCohortFormValues = {
        name: cohort.name,
        short_name: cohort.short_name || '',
        code: cohort.code || '',
        default_meeting_link: cohort.default_meeting_link || '',
        default_meeting_platform: cohort.default_meeting_platform || '',
        show_zoom_dial_in: cohort.show_zoom_dial_in ?? false,
        coach_immersion_date: parseDateTime(cohort.coach_immersion_date),
        participant_orientation_date: parseDateTime(
          cohort.participant_orientation_date
        ),
        cohort_wrap_up_date: parseDateOnly(cohort.cohort_wrap_up_date),
        start_date: parseDateOnly(cohort.start_date),
        end_date: parseDateOnly(cohort.end_date),
        participant_description: cohort.participant_description || '',
        matching_form_link: cohort.matching_form_link || '',
        matching_form_deadline: parseDateOnly(cohort.matching_form_deadline),
        info_session_date: parseDateTime(cohort.info_session_date),
        // MED-1350: ?? keeps the form controlled if the API omits a field.
        checkins: {
          introEnabled: cohort.intro_checkin_enabled ?? false,
          introWeek: cohort.intro_checkin_target_week ?? null,
          midpointEnabled: cohort.midpoint_checkin_enabled ?? false,
          midpointWeek: cohort.midpoint_checkin_target_week ?? null,
        },
      };
      form.setValues(loadedValues);

      // MED-1269: reset the Mantine dirty baseline to the just-loaded values so
      // form.isDirty() reflects real edits, not the empty→loaded transition (AC8).
      // Pass the values explicitly so the baseline is the loaded data, not a
      // possibly-stale snapshot from before setValues applied.
      form.resetDirty(loadedValues);
      loadedValuesRef.current = loadedValues;

      // Primary contacts
      const currentContactIds = cohort.primary_contacts?.map(c => c.id) || [];
      setSelectedContactIds(currentContactIds);
      setOriginalContactIds([...currentContactIds]);

      // Team assignments (keyed by job_title_id from job_title_assignments)
      const initial: Record<string, string | null> = {};
      if (
        cohort.job_title_assignments &&
        cohort.job_title_assignments.length > 0
      ) {
        for (const assignment of cohort.job_title_assignments) {
          initial[assignment.job_title_id] = assignment.user_id || null;
        }
      }
      setTeamAssignments(initial);
      setOriginalTeamAssignments({ ...initial });

      // Date constraints
      if (cohort.date_constraints && cohort.date_constraints.length > 0) {
        const loadedConstraints = cohort.date_constraints.map(c => ({
          id: c.id,
          startDate: parseDateOnly(c.start_date),
          endDate: parseDateOnly(c.end_date),
          label: c.label || '',
        }));
        setDateConstraints(loadedConstraints);
        setOriginalConstraintsKey(serializeConstraints(loadedConstraints));
      } else {
        setDateConstraints([]);
        setOriginalConstraintsKey('[]');
      }

      // Coach huddles
      const huddleDates = cohort.coach_huddle_dates || [];
      if (huddleDates.length > 0) {
        setCoachHuddles(
          huddleDates.map(d => ({
            id: generateId(),
            dateTime: parseDateTime(d),
          }))
        );
      } else {
        setCoachHuddles([]);
      }

      // Session dates
      // MED-1350: the change check compares against these same rows, so the
      // placeholder rows below do not count as a date change on open.
      let loadedSessionDates: SessionDateRow[] = [];
      if (cohort.sessions && cohort.sessions.length > 0) {
        loadedSessionDates = cohort.sessions.map(s => ({
          id: s.id,
          sessionNumber: s.session_number,
          date: parseDateOnly(s.scheduled_start),
          groupName: s.group_name,
        }));
      } else {
        // MED-369: Generate placeholder session date rows from modality session_count
        // so users can add dates post-creation when no sessions exist yet
        const groupCoachingModality = cohort.modalities?.find(
          m => m.modality_type === 'group_coaching'
        );
        const sessionCount =
          groupCoachingModality?.session_count ?? cohort.session_count ?? 0;
        if (sessionCount > 0) {
          loadedSessionDates = Array.from({ length: sessionCount }, (_, i) => ({
            id: generateId(),
            sessionNumber: i + 1,
            date: null,
          }));
        }
      }
      setSessionDates(loadedSessionDates);

      // Store original dates for change detection
      setOriginalDates({
        coach_immersion_date: parseDateTime(cohort.coach_immersion_date),
        participant_orientation_date: parseDateTime(
          cohort.participant_orientation_date
        ),
        cohort_wrap_up_date: parseDateOnly(cohort.cohort_wrap_up_date),
        coach_huddle_dates: (cohort.coach_huddle_dates || [])
          .map(d => parseDateTime(d)!)
          .filter(Boolean) as Date[],
        session_dates: loadedSessionDates,
      });

      // Initialize modality configurations
      if (cohort.modalities && cohort.modalities.length > 0) {
        const configs = cohort.modalities.map(m => ({
          id: m.id,
          modality_type: m.modality_type,
          session_count: m.session_count,
          session_frequency: m.session_frequency,
          session_duration_minutes: m.session_duration_minutes,
          sessionConfigs: (m.configuration?.session_configs ?? []).map(
            (c: {
              session_number: number;
              duration_minutes: number;
              one_on_one_type?: ConfigurableOneOnOneType;
              target_week?: number | null;
              is_scheduled?: boolean;
            }) => ({
              sessionNumber: c.session_number,
              durationMinutes: c.duration_minutes,
              oneOnOneType: c.one_on_one_type ?? DEFAULT_ONE_ON_ONE_TYPE,
              targetWeek: c.target_week ?? null,
              isScheduled: c.is_scheduled ?? false,
            })
          ),
          // MED-848 — pull through new persistent fields and the AC9 lock signal
          coaching_sub_type: m.coaching_sub_type ?? null,
          ai_features_enabled: m.ai_features_enabled ?? false,
          // MED-948 (E1.10a) — Live Session Delivery gate (AC7).
          live_session_delivery_enabled:
            m.live_session_delivery_enabled ?? false,
          // MED-1069 (E1.10c) — Commitment Tracking (group_coaching only, AC4).
          commitment_tracking_enabled: m.commitment_tracking_enabled ?? false,
          invites_sent: m.invites_sent ?? false,
          // MED-955 — second lock signal (session leaders designated)
          session_leaders_designated: m.session_leaders_designated ?? false,
        }));
        setModalityConfigs(configs);
        setOriginalModalityConfigs(
          configs.map(c => ({
            ...c,
            sessionConfigs: c.sessionConfigs.map(s => ({ ...s })),
          }))
        );
      } else {
        setModalityConfigs([]);
        setOriginalModalityConfigs([]);
      }

      fetchContacts();
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, cohort]);

  // ========================
  // Contact Management
  // ========================

  const handleRemoveContact = (contactId: string) => {
    setSelectedContactIds(prev => prev.filter(id => id !== contactId));
  };

  const handleAddContact = (contactId: string | null) => {
    if (contactId && !selectedContactIds.includes(contactId)) {
      setSelectedContactIds(prev => [...prev, contactId]);
    }
  };

  // ========================
  // Date Constraints CRUD
  // ========================

  const addDateConstraint = () => {
    setDateConstraints(prev => [
      ...prev,
      { id: generateId(), startDate: null, endDate: null, label: '' },
    ]);
  };

  const removeDateConstraint = (id: string) => {
    setDateConstraints(prev => prev.filter(c => c.id !== id));
  };

  const updateDateConstraint = (
    id: string,
    field: keyof ConstraintRow,
    value: Date | null | string
  ) => {
    setDateConstraints(prev =>
      prev.map(c => (c.id === id ? { ...c, [field]: value } : c))
    );
  };

  // ========================
  // Coach Huddles CRUD
  // ========================

  const addCoachHuddle = () => {
    setCoachHuddles(prev => [...prev, { id: generateId(), dateTime: null }]);
  };

  const removeCoachHuddle = (id: string) => {
    setCoachHuddles(prev => prev.filter(h => h.id !== id));
  };

  const updateCoachHuddle = (id: string, dateTime: Date | null) => {
    setCoachHuddles(prev =>
      prev.map(h => (h.id === id ? { ...h, dateTime } : h))
    );
  };

  // ========================
  // Session Dates
  // ========================

  const updateSessionDate = (sessionId: string, date: Date | null) => {
    setSessionDates(prev =>
      prev.map(s => (s.id === sessionId ? { ...s, date } : s))
    );
  };

  // ========================
  // Modality Config
  // ========================

  const updateModalityConfig = (
    modalityId: string,
    field: keyof ModalityConfigRow,
    value: number | null
  ) => {
    setModalityConfigs(prev =>
      prev.map(m => (m.id === modalityId ? { ...m, [field]: value } : m))
    );
  };

  // MED-848 / MED-948 / MED-1069 — typed updater for the per-modality settings fields.
  const updateModalitySettingsField = (
    modalityId: string,
    field:
      | 'coaching_sub_type'
      | 'ai_features_enabled'
      | 'live_session_delivery_enabled'
      | 'commitment_tracking_enabled',
    value: CoachingSubType | boolean | null
  ) => {
    setModalityConfigs(prev =>
      prev.map(m => {
        if (m.id !== modalityId) return m;
        const next = { ...m, [field]: value } as ModalityConfigRow;
        // MED-948 AC8 — turning the gate off also clears AI features locally so
        // the change-detection in handleSave picks up the AI reset and the UI
        // reflects it immediately (backend enforces the same invariant).
        if (field === 'live_session_delivery_enabled' && value === false) {
          next.ai_features_enabled = false;
        }
        return next;
      })
    );
  };

  const updateModalitySessionConfigs = (
    modalityId: string,
    sessionConfigs: ModalitySessionConfig[]
  ) => {
    setModalityConfigs(prev =>
      prev.map(m => (m.id === modalityId ? { ...m, sessionConfigs } : m))
    );
  };

  // ========================
  // Team Assignments
  // ========================

  const handleAssignmentChange = (
    jobTitleId: string,
    userId: string | null
  ) => {
    setTeamAssignments(prev => ({ ...prev, [jobTitleId]: userId }));
  };

  const handleAutoPopulate = () => {
    // Build lookup: job title name → user IDs with that role
    const roleToUsers = new Map<string, string[]>();
    for (const user of users) {
      if (user.job_title) {
        const existing = roleToUsers.get(user.job_title) || [];
        existing.push(user.id);
        roleToUsers.set(user.job_title, existing);
      }
    }

    // Fill only unassigned roles
    setTeamAssignments(prev => {
      const updated = { ...prev };
      for (const group of jobTitleGroups) {
        for (const title of group.titles) {
          if (updated[title.id]) continue;
          const matchingUsers = roleToUsers.get(title.name);
          if (matchingUsers && matchingUsers.length === 1) {
            updated[title.id] = matchingUsers[0];
          }
        }
      }
      return updated;
    });
  };

  // ========================
  // Save Handler
  // ========================

  // MED-1269: validation gate, run once before any section writes. Returns
  // false (and surfaces field errors) when the core form is invalid.
  const validateCore = (): boolean => {
    const validation = form.validate();
    if (validation.hasErrors) return false;
    // MED-1350: the target weeks of the check-ins that are on must fit the
    // program. Only a check-in the CM can see is checked.
    const checkinError = hasCheckinHost
      ? checkinsError(
          form.values.checkins,
          programWeeks(form.values.start_date, form.values.end_date)
        )
      : null;
    if (checkinError) {
      form.setFieldError('checkins', checkinError);
      return false;
    }
    if (wrapDateConflict) {
      form.setFieldError(
        'cohort_wrap_up_date',
        'Session dates extend past the cohort wrap date. Please update the wrap date.'
      );
      return false;
    }
    return true;
  };

  // MED-1269: commit the seven form-backed sections. Throws on failure so the
  // modal can report which sections saved (AC19); does NOT toast or close —
  // the modal owns the combined success message and close.
  const commitCore = async (
    regenerateDueDates: boolean = false,
    regenerateTeamAssignments: boolean = false
  ): Promise<string[]> => {
    const values = form.values;
    {
      // 1. Update cohort basic info and cohort dates
      const updateData = {
        name: values.name,
        short_name: values.short_name,
        code: values.code,
        // MED-1360: a changed optional field only; a cleared one goes as null.
        ...clearableCohortFields(values, loadedValuesRef.current ?? values),
        // MED-1360: the PUT never regenerates task due dates. On Yes this save
        // calls regenerateDueDates after the update; on No nothing does.
        regenerate_due_dates: false,
        start_date: toISODateString(values.start_date) || undefined,
        end_date: toISODateString(values.end_date) || undefined,
        coach_immersion_date: toISODateTimeString(values.coach_immersion_date),
        participant_orientation_date: toISODateTimeString(
          values.participant_orientation_date
        ),
        coach_huddle_dates: coachHuddles
          .filter(h => h.dateTime)
          .map(h => toISODateTimeString(h.dateTime)!),
        info_session_date: toISODateTimeString(values.info_session_date),
        // MED-1350: send the toggles with their weeks, and only on a change,
        // so a save of other fields never rewrites the check-ins.
        ...(form.isDirty('checkins')
          ? {
              intro_checkin_enabled: values.checkins.introEnabled,
              intro_checkin_target_week: values.checkins.introWeek,
              midpoint_checkin_enabled: values.checkins.midpointEnabled,
              midpoint_checkin_target_week: values.checkins.midpointWeek,
            }
          : {}),
        // MED-1411: only on a change, like the check-ins.
        ...(form.isDirty('show_zoom_dial_in')
          ? { show_zoom_dial_in: values.show_zoom_dial_in }
          : {}),
      };

      let updated = await cohortsApi.update(cohort.id, updateData);

      // 2. Update primary contacts
      updated = await cohortsApi.updatePrimaryContacts(
        cohort.id,
        selectedContactIds
      );

      // 3. Update team assignments via job title assignments
      const jobTitleAssignmentData = Object.entries(teamAssignments).map(
        ([job_title_id, user_id]) => ({
          job_title_id,
          user_id,
        })
      );
      await jobTitlesApi.updateCohortAssignments(
        cohort.id,
        jobTitleAssignmentData
      );

      // 4. Update date constraints
      const validConstraints = dateConstraints.filter(
        c => c.startDate && c.endDate
      );
      const constraintData: DateConstraintUpdate[] = validConstraints.map(
        c => ({
          start_date: toISODateString(c.startDate)!,
          end_date: toISODateString(c.endDate)!,
          label: c.label || undefined,
        })
      );
      updated = await cohortsApi.updateDateConstraints(
        cohort.id,
        constraintData
      );

      // 5. Update session dates if any have changed (skip if locked).
      // MED-1360: send them only on a real change, and pass the CM's answer,
      // because this call recalculates task due dates.
      if (!sessionDatesLocked) {
        const sessionUpdates = changedSessionUpdates(
          sessionDates,
          originalDates.session_dates
        );
        if (sessionUpdates.length > 0) {
          updated = await cohortsApi.updateSessions(
            cohort.id,
            sessionUpdates,
            regenerateDueDates
          );
        }
      }

      // MED-848 (AC8/AC9) — non-destructive PATCH of per-modality settings.
      // Runs BEFORE the destructive duration-change replace so original
      // modality IDs are still valid. Only PATCHes fields that actually
      // changed; backend rejects sub-type updates once invites_sent=true.
      for (const config of modalityConfigs) {
        const original = originalModalityConfigs.find(o => o.id === config.id);
        if (!original) continue;

        const aiChanged =
          original.ai_features_enabled !== config.ai_features_enabled;
        // MED-948 (E1.10a) — Live Session Delivery gate change (AC7).
        const lsdChanged =
          original.live_session_delivery_enabled !==
          config.live_session_delivery_enabled;
        // MED-1069 (E1.10c) — Commitment Tracking change (group_coaching, AC4).
        const commitmentChanged =
          original.commitment_tracking_enabled !==
          config.commitment_tracking_enabled;
        const subTypeChanged =
          config.modality_type === 'group_coaching' &&
          !config.invites_sent &&
          original.coaching_sub_type !== config.coaching_sub_type &&
          config.coaching_sub_type !== null;

        if (!aiChanged && !lsdChanged && !commitmentChanged && !subTypeChanged)
          continue;

        await cohortsApi.updateModalitySettings(cohort.id, config.id, {
          ...(aiChanged
            ? { ai_features_enabled: config.ai_features_enabled }
            : {}),
          ...(lsdChanged
            ? {
                live_session_delivery_enabled:
                  config.live_session_delivery_enabled,
              }
            : {}),
          ...(commitmentChanged
            ? {
                commitment_tracking_enabled: config.commitment_tracking_enabled,
              }
            : {}),
          ...(subTypeChanged
            ? { coaching_sub_type: config.coaching_sub_type }
            : {}),
        });
      }

      // 6. Update modality configurations if changed
      if (hasDurationChanges && canEditSessionConfig) {
        const modalityData: ModalityUpdate[] = modalityConfigs.map(m => ({
          modality_type: m.modality_type,
          session_count: m.session_count,
          session_frequency: m.session_frequency,
          session_duration_minutes: m.session_duration_minutes,
          configuration:
            m.modality_type === 'one_on_one' && m.sessionConfigs.length > 0
              ? {
                  session_configs: m.sessionConfigs.map(sc => ({
                    session_number: sc.sessionNumber,
                    duration_minutes: sc.durationMinutes,
                    one_on_one_type: sc.oneOnOneType,
                    target_week: sc.targetWeek,
                  })),
                }
              : undefined,
          // MED-848 — preserve the new fields across the destructive replace.
          coaching_sub_type:
            m.modality_type === 'group_coaching' ? m.coaching_sub_type : null,
          ai_features_enabled: m.ai_features_enabled,
          // MED-948 (E1.10a) — preserve the gate too, else a duration change
          // would silently reset it to the column default (false).
          live_session_delivery_enabled: m.live_session_delivery_enabled,
        }));
        updated = await cohortsApi.updateModalities(cohort.id, modalityData);
      }

      // 7. Regenerate due dates and/or team assignments if requested
      const messages: string[] = [];

      if (regenerateDueDates && hasDateChanges) {
        const result = await cohortsApi.regenerateDueDates(cohort.id);
        if (result.createdCount) {
          messages.push(`${result.createdCount} session tasks created`);
        }
        messages.push(`${result.updatedCount} due dates recalculated`);
      }

      if (regenerateTeamAssignments && hasTeamAssignmentChanges) {
        const result = await cohortsApi.regenerateTeamAssignments(
          cohort.id,
          changedJobTitleIds
        );
        messages.push(
          `${result.updatedCount} task assignments were reassigned`
        );
      }

      // Final: re-fetch complete cohort to ensure all fields (including job_title_assignments)
      // are present, since some intermediate endpoints return partial responses
      updated = await cohortsApi.getById(cohort.id);

      onUpdate(updated);
      return messages;
    }
  };

  // ========================
  // Derived Data
  // ========================

  const userOptions = users.map(user => ({
    value: user.id,
    label: `${user.name} (${user.email})`,
  }));

  const availableContacts = contacts.filter(
    c => !selectedContactIds.includes(c.id)
  );
  const selectedContacts = contacts.filter(c =>
    selectedContactIds.includes(c.id)
  );
  const rawLoading = loadingContacts || loadingUsers;
  // Show the full-form spinner on the first load only. A reload after the
  // details save must not remount the sections and lose their pending state.
  const sawLoadingRef = useRef(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  useEffect(() => {
    if (!opened) {
      sawLoadingRef.current = false;
      setHasLoadedOnce(false);
    } else if (rawLoading) {
      sawLoadingRef.current = true;
    } else if (sawLoadingRef.current) {
      setHasLoadedOnce(true);
    }
  }, [opened, rawLoading]);
  const isLoading = rawLoading && !hasLoadedOnce;

  // MED-1269 (AC8/AC9): value-based dirty across the seven form-backed sections.
  // The three converted sections (feature toggles, email, time slots) report
  // their own dirty via section controllers in the modal.
  const contactsChanged = useMemo(() => {
    if (selectedContactIds.length !== originalContactIds.length) return true;
    const orig = new Set(originalContactIds);
    return selectedContactIds.some(id => !orig.has(id));
  }, [selectedContactIds, originalContactIds]);

  const coreDirty = useMemo(() => {
    return (
      form.isDirty() ||
      hasDateChanges ||
      hasTeamAssignmentChanges ||
      hasModalitySettingsChanges ||
      contactsChanged ||
      serializeConstraints(dateConstraints) !== originalConstraintsKey
    );
  }, [
    form,
    hasDateChanges,
    hasTeamAssignmentChanges,
    hasModalitySettingsChanges,
    contactsChanged,
    dateConstraints,
    originalConstraintsKey,
  ]);

  return {
    form,
    isLoading,
    coreDirty,
    contacts,
    selectedContactIds,
    selectedContacts,
    availableContacts,
    handleAddContact,
    handleRemoveContact,
    teamAssignments,
    handleAssignmentChange,
    handleAutoPopulate,
    users,
    userOptions,
    jobTitleGroups,
    dateConstraints,
    addDateConstraint,
    removeDateConstraint,
    updateDateConstraint,
    coachHuddles,
    addCoachHuddle,
    removeCoachHuddle,
    updateCoachHuddle,
    sessionDates,
    updateSessionDate,
    sessionDatesLocked,
    totalSessionCount,
    uniqueSessionNumbers,
    modalityConfigs,
    updateModalityConfig,
    updateModalitySessionConfigs,
    updateModalitySettingsField,
    canEditSessionConfig,
    showDurationWarning,
    hasDateChanges,
    hasTeamAssignmentChanges,
    wrapDateConflict,
    validateCore,
    commitCore,
  };
}
